import React, { useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

export interface ComplexGraphData {
  labels: string[];
  teams?: string[];
  occurrences?: number[];
  stat_columns: string[];
  stat_values: Record<string, number[]>;
  axis_labels?: Record<string, string>;
  total_records?: number;
  chartReady?: boolean;
  query_type?: string;
}

const SCATTER_COLORS = [
  "#6366F1", "#EC4899", "#10B981", "#F59E0B", "#3B82F6",
  "#F97316", "#8B5CF6", "#4ADE80", "#06B6D4", "#EF4444",
];

const fmtLabel = (key: string, axis_labels?: Record<string, string>) => {
  if (axis_labels?.[key]) return axis_labels[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const colorAt = (i: number) => SCATTER_COLORS[i % SCATTER_COLORS.length];

const generateAxisCategories = (values: number[]): { categories: string[]; min: number; max: number } => {
  if (values.length === 0) return { categories: ["0", "10"], min: 0, max: 10 };

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  if (range === 0) return { categories: [String(minValue)], min: minValue, max: minValue };

  let step = 1;
  if (range <= 20) step = 1;
  else if (range <= 50) step = 5;
  else if (range <= 100) step = 10;
  else if (range <= 200) step = 20;
  else if (range <= 500) step = 50;
  else if (range <= 1000) step = 100;
  else if (range <= 2000) step = 200;
  else if (range <= 5000) step = 500;
  else step = 1000;

  const categories: string[] = [];
  const start = Math.floor(minValue / step) * step;
  const end = Math.ceil(maxValue / step) * step;
  for (let i = start; i <= end; i += step) categories.push(String(i));

  return {
    categories,
    min: Math.min(...categories.map(Number)),
    max: Math.max(...categories.map(Number)),
  };
};

// Build dense-rank groups sorted by descending occurrence count
const buildDenseRankGroups = (
  uniquePlayers: string[],
  labels: string[],
  occurrences?: number[]
) => {
  if (!occurrences || occurrences.length === 0) {
    return [{
      rank: 1,
      occurrenceCount: null as number | null,
      players: uniquePlayers.map((name, colorIndex) => ({
        name,
        firstLabelIndex: labels.indexOf(name),
        colorIndex,
      })),
    }];
  }

  const playerEntries = uniquePlayers.map((name, colorIndex) => {
    const firstLabelIndex = labels.indexOf(name);
    const occ = occurrences[firstLabelIndex] ?? 0;
    return { name, firstLabelIndex, occ, colorIndex };
  });

  playerEntries.sort((a, b) => b.occ - a.occ);

  const groups: {
    rank: number;
    occurrenceCount: number;
    players: { name: string; firstLabelIndex: number; colorIndex: number }[];
  }[] = [];

  let currentRank = 0;
  let lastOcc: number | null = null;

  for (const entry of playerEntries) {
    if (entry.occ !== lastOcc) {
      currentRank++;
      lastOcc = entry.occ;
      groups.push({ rank: currentRank, occurrenceCount: entry.occ, players: [] });
    }
    groups[groups.length - 1].players.push({
      name: entry.name,
      firstLabelIndex: entry.firstLabelIndex,
      colorIndex: entry.colorIndex,
    });
  }

  return groups;
};

// ── Custom Dropdown ────────────────────────────────────────────────────────────
const RANK_OPTIONS: { value: 5 | 10; label: string; sublabel: string }[] = [
  { value: 5,  label: "Top 5",  sublabel: "Show top 5 dense ranks"  },
  { value: 10, label: "Top 10", sublabel: "Show top 10 dense ranks" },
];

interface RankDropdownProps {
  value: 5 | 10;
  onChange: (v: 5 | 10) => void;
  playerCount: number;
}

const RankDropdown: React.FC<RankDropdownProps> = ({ value, onChange, playerCount }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = RANK_OPTIONS.find((o) => o.value === value)!;

  return (
    <div ref={ref} style={{ position: "relative", userSelect: "none" }}>
      {/* Trigger button */}
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "7px 12px 7px 14px",
          borderRadius: "10px",
          cursor: "pointer",
          background: open
            ? "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)"
            : "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
          boxShadow: open
            ? "0 4px 16px rgba(37,99,235,0.45)"
            : "0 2px 8px rgba(37,99,235,0.28)",
          border: "1.5px solid rgba(255,255,255,0.18)",
          transition: "all 0.18s ease",
          minWidth: "130px",
        }}
      >
        {/* Icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "12px", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
            {selected.label}
          </div>
          <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.72)", lineHeight: 1.1 }}>
            {playerCount} player{playerCount !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Caret */}
        <svg
          width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          right: 0,
          zIndex: 999,
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
          minWidth: "170px",
          padding: "6px",
        }}>
          {/* Header */}
          <div style={{
            padding: "6px 10px 8px",
            fontSize: "9px",
            fontWeight: 700,
            color: "#94A3B8",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            Dense Rank Filter
          </div>

          {RANK_OPTIONS.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "9px 10px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: isSelected
                    ? "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)"
                    : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "transparent";
                }}
              >
                {/* Check mark */}
                <div style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  border: `2px solid ${isSelected ? "#3B82F6" : "#CBD5E1"}`,
                  background: isSelected ? "#3B82F6" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}>
                  {isSelected && (
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                      <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                <div>
                  <div style={{
                    fontSize: "12px",
                    fontWeight: isSelected ? 700 : 600,
                    color: isSelected ? "#1D4ED8" : "#374151",
                    lineHeight: 1.2,
                  }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: "10px", color: "#94A3B8", lineHeight: 1.2 }}>
                    {opt.sublabel}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ── End Custom Dropdown ────────────────────────────────────────────────────────

const ComplexGraph: React.FC<ComplexGraphData> = ({
  labels,
  teams,
  occurrences,
  stat_columns,
  stat_values,
  axis_labels,
  query_type,
  chartReady = true,
}) => {
  const [xKey, yKey] = stat_columns;
  const xValues = stat_values[xKey] ?? [];
  const yValues = stat_values[yKey] ?? [];

  const [selectedPlayers, setSelectedPlayers] = useState<Set<string> | null>(null);
  // Default to top 5 dense ranks
  const [rankFilter, setRankFilter] = useState<5 | 10>(5);

  const uniquePlayers = useMemo(() => {
    const seen = new Set<string>();
    const unique: string[] = [];
    labels.forEach((label) => {
      if (!seen.has(label)) { seen.add(label); unique.push(label); }
    });
    return unique;
  }, [labels]);

  const playerColorMap = useMemo(() => {
    const map = new Map<string, string>();
    uniquePlayers.forEach((player, i) => map.set(player, colorAt(i)));
    return map;
  }, [uniquePlayers]);

  const denseRankGroups = useMemo(
    () => buildDenseRankGroups(uniquePlayers, labels, occurrences),
    [uniquePlayers, labels, occurrences]
  );

  // Filter to selected top-N dense ranks
  const visibleRankGroups = useMemo(
    () => denseRankGroups.filter((g) => g.rank <= rankFilter),
    [denseRankGroups, rankFilter]
  );

  // All players within visible ranks
  const visiblePlayerNames = useMemo(() => {
    const set = new Set<string>();
    visibleRankGroups.forEach((g) => g.players.forEach((p) => set.add(p.name)));
    return set;
  }, [visibleRankGroups]);

  const visiblePlayerCount = useMemo(
    () => visibleRankGroups.reduce((acc, g) => acc + g.players.length, 0),
    [visibleRankGroups]
  );

  const selectAllPlayers = () => setSelectedPlayers(null);
  const clearAll = () => setSelectedPlayers(new Set());

  const togglePlayer = (playerName: string) => {
    setSelectedPlayers((prev) => {
      if (prev === null) return new Set([playerName]);
      const next = new Set(prev);
      if (next.has(playerName)) next.delete(playerName);
      else next.add(playerName);
      return next;
    });
  };

  const xRange = useMemo(() => {
    const vals = uniquePlayers.map((p) => xValues[labels.indexOf(p)] ?? 0);
    return (Math.max(...vals) - Math.min(...vals)) || 1;
  }, [uniquePlayers, labels, xValues]);

  const yRange = useMemo(() => {
    const vals = uniquePlayers.map((p) => yValues[labels.indexOf(p)] ?? 0);
    return (Math.max(...vals) - Math.min(...vals)) || 1;
  }, [uniquePlayers, labels, yValues]);

  const series = useMemo(() => {
    const playersToShow: Set<string> =
      selectedPlayers === null ? new Set(uniquePlayers) : selectedPlayers;

    const pointOccupancy = new Map<string, number>();
    const dx = xRange * 0.02;
    const dy = yRange * 0.02;
    const offsetPattern: [number, number][] = [
      [0, 0], [dx, 0], [-dx, 0], [0, dy], [0, -dy],
    ];

    return uniquePlayers
      .filter((name) => playersToShow.has(name) && visiblePlayerNames.has(name))
      .map((playerName) => {
        const idx = labels.indexOf(playerName);
        const rawX = xValues[idx] ?? 0;
        const rawY = yValues[idx] ?? 0;
        const pointKey = `${rawX}_${rawY}`;
        const slotIndex = pointOccupancy.get(pointKey) ?? 0;
        pointOccupancy.set(pointKey, slotIndex + 1);
        const [ox, oy] = offsetPattern[slotIndex % offsetPattern.length];
        return { name: playerName, data: [[rawX + ox, rawY + oy]] as [number, number][] };
      });
  }, [labels, xValues, yValues, uniquePlayers, selectedPlayers, xRange, yRange, visiblePlayerNames]);

  const xAxisData = useMemo(() => generateAxisCategories(xValues), [xValues]);
  const yAxisData = useMemo(() => generateAxisCategories(yValues), [yValues]);

  const options: ApexOptions = {
    chart: {
      type: "scatter",
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: true, speed: 600 },
      zoom: { enabled: false, type: "xy" },
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },
    colors: series.map((s) => playerColorMap.get(s.name) || "#999"),
    markers: { size: 10, strokeWidth: 2, strokeColors: "#ffffff", hover: { sizeOffset: 4 } },
    xaxis: {
      title: { text: fmtLabel(xKey, axis_labels), style: { fontSize: "12px", fontWeight: 600, color: "#374151" } },
      type: "numeric",
      min: xAxisData.min,
      max: xAxisData.max,
      tickAmount: xAxisData.categories.length - 1,
      labels: {
        style: { colors: "#6B7280", fontSize: "11px" },
        formatter: (val: string | number) => Math.round(typeof val === "string" ? parseFloat(val) : val).toLocaleString(),
      },
    },
    yaxis: {
      title: { text: fmtLabel(yKey, axis_labels), style: { fontSize: "12px", fontWeight: 600, color: "#374151" } },
      type: "numeric",
      min: yAxisData.min,
      max: yAxisData.max,
      tickAmount: yAxisData.categories.length - 1,
      labels: {
        style: { colors: "#6B7280", fontSize: "11px" },
        formatter: (val: string | number) => Math.round(typeof val === "string" ? parseFloat(val) : val).toLocaleString(),
      },
    },
    grid: { borderColor: "#E5E7EB", xaxis: { lines: { show: true } }, yaxis: { lines: { show: true } } },
    legend: { show: false },
    tooltip: {
      custom: ({ seriesIndex, w }: any) => {
        const name = w.globals.seriesNames[seriesIndex] as string;
        const originalIndex = labels.indexOf(name);
        const color = playerColorMap.get(name) || "#999";
        const team = teams?.[originalIndex] ?? "";
        const occ = occurrences?.[originalIndex];

        const statsRows = stat_columns.map((key) => {
          const value = stat_values[key]?.[originalIndex] ?? 0;
          return `
            <div style="display:flex;justify-content:space-between;gap:16px;">
              <span style="color:#555;font-weight:500;">${fmtLabel(key, axis_labels)}:</span>
              <span style="font-weight:700;color:#333">${Number(value).toLocaleString()}</span>
            </div>`;
        }).join("");

        return `
          <div style="background:#d6d4d4;color:#333;border:1px solid ${color};border-radius:12px;
            padding:12px 16px;font-family:'Inter',sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.15);
            min-width:200px;max-width:260px;">
            <p style="margin:0 0 4px;font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name}</p>
            ${team ? `<p style="margin:0 0 10px;font-size:11px;color:#666;">${team}</p>` : ""}
            <div style="display:flex;flex-direction:column;gap:6px;font-size:12px;">
              ${statsRows}
              ${occ !== undefined ? `
                <div style="border-top:1px solid #ccc;padding-top:8px;margin-top:4px;">
                  <span style="color:#888;font-size:11px;">Occurrences: <b style="color:${color}">${occ}</b></span>
                </div>` : ""}
            </div>
          </div>`;
      },
    },
  };

  if (!chartReady) return null;

  if (stat_columns.length > 2) {
    return (
      <div style={{
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#FFFFFF",
        borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", padding: "40px", gap: "16px",
      }}>
        <div style={{ fontSize: "48px" }}>📊</div>
        <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1E293B", fontFamily: "'Inter', sans-serif" }}>
          Graph Not Supported
        </h3>
        <p style={{ margin: 0, fontSize: "14px", color: "#6B7280", textAlign: "center", maxWidth: "320px", fontFamily: "'Inter', sans-serif", lineHeight: 1.6 }}>
          This chart supports up to <strong>2 stat columns</strong>, but{" "}
          <strong>{stat_columns.length}</strong> were provided.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column",
      background: "#FFFFFF", borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1)", overflow: "hidden",
    }}>

      {/* ── Header ── */}
      <div style={{
        padding: "14px 20px",
        background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
        borderBottom: "1px solid #E2E8F0",
      }}>

        {/* Controls row */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
          <button onClick={selectAllPlayers} style={buttonStyle("#3B82F6")}>Select All</button>
          <button onClick={clearAll} style={buttonStyle("#EF4444")}>Clear</button>

          {/* Rank filter dropdown — pushed to right */}
          <div style={{ marginLeft: "auto" }}>
            <RankDropdown
              value={rankFilter}
              onChange={(v) => {
                setRankFilter(v);
                setSelectedPlayers(null); // reset selection on filter change
              }}
              playerCount={visiblePlayerCount}
            />
          </div>
        </div>

        {/* Legend — one row per dense rank group, no rank labels */}
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          {visibleRankGroups.map((group) => (
            <div
              key={group.rank}
              style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}
            >
              {group.players.map((player) => {
                const isActive =
                  selectedPlayers === null ? true : selectedPlayers.has(player.name);
                const color = colorAt(player.colorIndex);
                const team = teams?.[player.firstLabelIndex];

                return (
                  <div
                    key={player.name}
                    onClick={() => togglePlayer(player.name)}
                    title={player.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "4px 9px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      background: isActive
                        ? `linear-gradient(135deg, ${color}22 0%, ${color}0f 100%)`
                        : "#F8FAFC",
                      border: `1.5px solid ${isActive ? color : "#E2E8F0"}`,
                      boxShadow: isActive ? `0 2px 6px ${color}20` : "none",
                      opacity: isActive ? 1 : 0.4,
                      transition: "all 0.18s ease",
                    }}
                  >
                    <div style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: color, flexShrink: 0,
                    }} />
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                      <span style={{
                        fontSize: "11px", fontWeight: 600,
                        color: isActive ? "#1E293B" : "#94A3B8",
                        transition: "color 0.18s",
                      }}>
                        {player.name}
                      </span>
                      {team && (
                        <span style={{ fontSize: "9px", color: "#9CA3AF" }}>{team}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Chart Area ── */}
      <div style={{ flex: 1, padding: "24px", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
        <div style={{ width: "100%", height: "100%", minHeight: "300px" }}>
          <ReactApexChart
            type="scatter"
            options={options}
            series={series as any}
            height="100%"
            width="100%"
          />
        </div>
      </div>
    </div>
  );
};

const buttonStyle = (color: string) => ({
  padding: "7px 14px", fontSize: "12px", fontWeight: 600, color,
  background: `${color}10`, border: `1px solid ${color}30`,
  borderRadius: "9px", cursor: "pointer", fontFamily: "'Inter', sans-serif",
  display: "flex", alignItems: "center", gap: "6px", transition: "all 0.2s ease",
});

export default ComplexGraph;